import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class ImageService {

  constructor() { }


  // Convierte base64 a Blob
  base64ToBlob(base64: string): Blob {
    const parts = base64.split(';base64,');
    const contentType = parts[0].split(':')[1];
    const byteCharacters = atob(parts[1]);
    const byteNumbers = new Array(byteCharacters.length).fill(0).map((_, i) => byteCharacters.charCodeAt(i));
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: contentType });
  }
  
  // Redimensionar a 250x250 usando canvas
  resizeImageBase64(base64: string, width: number, height: number): Promise<string> {
    return new Promise((resolve) => {
      const img = new Image();
      img.src = base64;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx?.drawImage(img, 0, 0, width, height);
        const resizedBase64 = canvas.toDataURL('image/jpeg', 0.85);
        resolve(resizedBase64);
      };
    });
  }

  /**
   * Comprime una imagen para adjuntos (soporte, etc.):
   * - limita el lado mayor (por defecto 1920px)
   * - baja calidad de forma progresiva hasta quedar bajo maxBytes
   * - el usuario no necesita reducir el peso manualmente
   */
  async compressForUpload(
    file: File,
    options?: { maxBytes?: number; maxEdge?: number }
  ): Promise<File> {
    const maxBytes = options?.maxBytes ?? 4.5 * 1024 * 1024;
    const maxEdge = options?.maxEdge ?? 1920;
    const allowed = new Set(['image/jpeg', 'image/png', 'image/webp']);
    if (!allowed.has(file.type)) {
      throw new Error('FORMAT');
    }

    // Si ya es liviana, no tocamos (evita recomprimir innecesariamente).
    if (file.size <= maxBytes && file.size <= 1.2 * 1024 * 1024) {
      return file;
    }

    const loaded = await this.loadImageDrawer(file);
    try {
      const scale = Math.min(1, maxEdge / Math.max(loaded.width, loaded.height));
      const width = Math.max(1, Math.round(loaded.width * scale));
      const height = Math.max(1, Math.round(loaded.height * scale));

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        throw new Error('CANVAS');
      }
      loaded.draw(ctx, width, height);

      const qualities = [0.82, 0.72, 0.62, 0.52, 0.42];
      let blob: Blob | null = null;
      for (const q of qualities) {
        blob = await this.canvasToBlob(canvas, 'image/jpeg', q);
        if (blob && blob.size <= maxBytes) break;
      }

      if (!blob) {
        throw new Error('ENCODE');
      }

      // Último recurso: reducir un poco más el lienzo si sigue pesada.
      if (blob.size > maxBytes) {
        const shrink = 0.75;
        canvas.width = Math.max(1, Math.round(width * shrink));
        canvas.height = Math.max(1, Math.round(height * shrink));
        loaded.draw(ctx, canvas.width, canvas.height);
        blob = await this.canvasToBlob(canvas, 'image/jpeg', 0.45);
      }

      if (!blob || blob.size > maxBytes) {
        throw new Error('TOO_LARGE');
      }

      const baseName = (file.name || 'captura').replace(/\.[^.]+$/, '');
      return new File([blob], `${baseName}.jpg`, {
        type: 'image/jpeg',
        lastModified: Date.now(),
      });
    } finally {
      loaded.dispose?.();
    }
  }

  private async loadImageDrawer(file: File): Promise<{
    width: number;
    height: number;
    draw: (ctx: CanvasRenderingContext2D, w: number, h: number) => void;
    dispose?: () => void;
  }> {
    try {
      const bitmap = await createImageBitmap(file);
      return {
        width: bitmap.width,
        height: bitmap.height,
        draw: (ctx, w, h) => ctx.drawImage(bitmap, 0, 0, w, h),
        dispose: () => bitmap.close(),
      };
    } catch {
      const dataUrl = await this.readFileAsDataUrl(file);
      const img = await this.loadHtmlImage(dataUrl);
      return {
        width: img.naturalWidth,
        height: img.naturalHeight,
        draw: (ctx, w, h) => ctx.drawImage(img, 0, 0, w, h),
      };
    }
  }

  private readFileAsDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = () => reject(new Error('READ'));
      reader.readAsDataURL(file);
    });
  }

  private loadHtmlImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('IMAGE'));
      img.src = src;
    });
  }

  private canvasToBlob(
    canvas: HTMLCanvasElement,
    type: string,
    quality: number
  ): Promise<Blob | null> {
    return new Promise((resolve) => {
      canvas.toBlob((b) => resolve(b), type, quality);
    });
  }

}
